import { HTMLAttributes, useContext, useEffect, useMemo, useState } from 'react'

import { Button } from '@hhgtech/hhg-components'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import ErrorBoundary from 'components/templates/navigatorLayout/errorBoundary'
import { domainLocales } from 'domainLocales'
import { useEmbeddedCommunity } from 'hooks/useEmbeddedCommunity'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  EmbeddedCommunityType,
  TogetherCommunity,
  TogetherPost,
  TogetherTopic,
} from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { CommunitySection } from './communitySection'
import { getCommunityInfo, getPostList, getTopicInfo } from './helpers'
import { StyledEmbeddedCommunity } from './index.styled'
import { PostSection } from './postSection'

type EmbeddedCommunityProps = {
  community?: EmbeddedCommunityType
  title?: string
  description?: string
  titleMember?: string
  titleGuest?: string
  descriptionMember?: string
  descriptionGuest?: string
  articleId?: string
  actionButton?: React.ReactNode
  hideButton?: boolean
} & HTMLAttributes<HTMLDivElement>

export const EmbeddedCommunityMain = ({
  community: _community,
  title: _title,
  description: _description,
  titleMember,
  titleGuest,
  descriptionGuest,
  descriptionMember,
  articleId,
  actionButton,
  hideButton,
  ...rest
}: EmbeddedCommunityProps) => {
  const { locale } = useRouterExtend()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)

  const [community, setCommunity] = useState<TogetherCommunity>()
  const [topics, setTopics] = useState<TogetherTopic[]>()
  const [posts, setPosts] = useState<TogetherPost[]>()
  const [isFetching, setIsFetching] = useState(false)

  const message = useMemo(() => {
    if (typeof _description !== 'undefined') return _description
    if (isLoggedIn) {
      if (community?.joined)
        return _description || descriptionMember || community?.description
      return _description || descriptionGuest || community?.description
    } else {
      return _description || descriptionGuest || community?.description
    }
  }, [
    isLoggedIn,
    community?.joined,
    community?.description,
    descriptionMember,
    descriptionGuest,
    _description,
  ])
  const title = useMemo(() => {
    if (isLoggedIn) {
      if (community?.joined) return _title || titleMember || community?.name
      return _title || titleGuest || community?.name
    } else {
      return _title || titleGuest || community?.name
    }
  }, [
    isLoggedIn,
    community?.joined,
    community?.name,
    titleGuest,
    titleMember,
    _title,
  ])

  useEffect(() => {
    setIsFetching(true)
    const query = {
      communityId: _community?.community,
      websiteUrl: `https://${domainLocales[locale]}`,
      articleId,
    }
    Promise.all([
      getCommunityInfo(query, callApiNew).then((community) => {
        setCommunity(community)
      }),
      getTopicInfo(query, callApiNew).then((topics) => {
        setTopics(topics)
      }),
      getPostList(query, callApiNew).then((posts) => {
        setPosts(posts)
      }),
    ])
      .then(() => {
        setIsFetching(false)
      })
      .catch((error) => {
        console.error(error)
        setIsFetching(false)
      })
  }, [_community?.community])

  return (
    <ErrorBoundary>
      <div
        style={{
          height: 500,
          display: isFetching ? 'block' : 'none',
        }}
      ></div>
      <StyledEmbeddedCommunity data-fetching={isFetching} {...rest}>
        <CommunitySection
          community={community}
          topics={topics}
          title={title}
          message={message}
          actionButton={actionButton}
        ></CommunitySection>
        <PostSection
          isJoined={community?.joined}
          posts={posts}
          communitySlug={community?.slug}
          hideButton={hideButton}
        />
      </StyledEmbeddedCommunity>
    </ErrorBoundary>
  )
}

const EmbeddedCommunityHealthTool = (props: EmbeddedCommunityProps) => {
  const { community } = useEmbeddedCommunity({
    communityId: props.community?.community,
  })
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()

  return (
    <EmbeddedCommunityMain
      hideButton
      title={props.community?.title}
      description={props.community?.description}
      actionButton={
        <a href={`/community/${community?.slug || ''}`}>
          <Button
            size="md"
            color="success"
            className="ask"
            isBlock={isMobile}
            icon={
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M14.9852 4.49251C14.7904 4.49832 14.6054 4.57975 14.4696 4.71956L6.74988 12.4393L4.28015 9.96956C4.21104 9.89758 4.12827 9.84012 4.03667 9.80053C3.94508 9.76094 3.8465 9.74003 3.74672 9.73901C3.64694 9.73799 3.54796 9.7569 3.45558 9.79462C3.36319 9.83233 3.27926 9.8881 3.20871 9.95866C3.13815 10.0292 3.08238 10.1132 3.04466 10.2055C3.00694 10.2979 2.98804 10.3969 2.98905 10.4967C2.99007 10.5965 3.01098 10.695 3.05057 10.7866C3.09016 10.8782 3.14763 10.961 3.21961 11.0301L6.21961 14.0301C6.36026 14.1707 6.551 14.2497 6.74988 14.2497C6.94876 14.2497 7.1395 14.1707 7.28015 14.0301L15.5302 5.78011C15.6385 5.67476 15.7126 5.53913 15.7425 5.39099C15.7725 5.24285 15.757 5.08912 15.6981 4.94993C15.6392 4.81074 15.5396 4.69259 15.4124 4.61097C15.2852 4.52935 15.1363 4.48806 14.9852 4.49251Z"
                  fill="#008C50"
                />
              </svg>
            }
          >
            {f({
              id: 'together.embed.topicCard.joinDiscussion',
              defaultMessage: 'Join Discussion',
            })}
          </Button>
        </a>
      }
      {...props}
    />
  )
}
export interface CompoundedComponent
  extends React.ForwardRefExoticComponent<EmbeddedCommunityProps> {
  HealthTool: typeof EmbeddedCommunityHealthTool
}

const EmbeddedCommunity = EmbeddedCommunityMain as CompoundedComponent

EmbeddedCommunity.HealthTool = EmbeddedCommunityHealthTool
export { EmbeddedCommunity }
